function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function toNumber(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function toBool(v, fallback = false) {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

function normalizeKey(v) {
  return String(v || '')
    .trim()
    .toLowerCase();
}

function formatLimit(isUnlimited, value, t) {
  if (isUnlimited) return t('p.pricingPage.limits.unlimited', { defaultValue: 'Unlimited' });
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : '0';
}

// Bullets that don't map to one gated FeatureNode on the backend (either
// not gated as a discrete node, or span two unrelated nodes): always shown
// checked, no click-to-scroll-into-comparison-table behavior (key: null).
// sortOrder values are chosen to land in the right spot against the real
// primemoq.app/pricing bullet order; they're interleaved with whatever
// sortOrder the backend's 13 real curated nodes carry.
function buildStaticFreeBullets(t) {
  return [
    {
      key: 'static_unlimited_rules',
      label: t('p.pricingPage.static.unlimitedRules', { defaultValue: 'Unlimited rules' }),
      sortOrder: -2,
    },
    {
      key: 'static_multiples_repurchase',
      label: t('p.pricingPage.static.multiplesRepurchase', {
        defaultValue: 'Multiples and repurchase limits',
      }),
      sortOrder: 15,
    },
  ];
}

// `orderIndex`, when given, is a Map<id, position> shared across every plan
// (see createPlansAdaptor below) so every card's checklist lines up
// row-for-row instead of each plan re-sorting independently by its own
// ok values, which breaks the visual correspondence between columns (and
// with the Plans Comparison table, which reads this same order).
function buildFeaturesUiFromPricingNodeStatuses(planRaw, t, orderIndex) {
  const fromNodes = safeArray(planRaw?.pricingNodeStatuses)
    .filter((n) => !n.parentNodeKey)
    .map((n) => ({
      id: n.nodeKey,
      featureId: n.nodeKey,
      key: `feature_${n.nodeKey}`,
      label: n.label,
      ok: n.ok === true,
      isComingSoon: false,
      sortOrder: toNumber(n.sortOrder, 0),
    }));

  const statics = buildStaticFreeBullets(t).map((b) => ({
    id: b.key,
    featureId: b.key,
    key: null,
    label: b.label,
    ok: true,
    isComingSoon: false,
    sortOrder: b.sortOrder,
  }));

  const all = [...fromNodes, ...statics];

  if (orderIndex) {
    return all.sort((a, b) => {
      const ai = orderIndex.has(a.id) ? orderIndex.get(a.id) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b.id) ? orderIndex.get(b.id) : Number.MAX_SAFE_INTEGER;
      return ai - bi;
    });
  }

  // No shared order yet, this call is what defines it (for the lowest-tier
  // plan): included features first, then excluded ones, each group keeping
  // the curated order, matching the standard pricing-page convention (what
  // you get, then what you don't).
  return all.sort((a, b) => {
    if (a.ok !== b.ok) return a.ok ? -1 : 1;
    return a.sortOrder - b.sortOrder;
  });
}

function isUltimatePlan(p) {
  const name = String(p?.name || '').toLowerCase();
  return name === 'ultimate' || name.includes('ultimate');
}

function buildUltimatePerks(planRaw, t) {
  const features = Array.isArray(planRaw?.features) ? planRaw.features : [];

  const cleaned = features.map((x) => String(x || '').trim()).filter(Boolean);

  if (cleaned.length) {
    return cleaned.map((raw) => {
      const isComingSoon = raw.endsWith(' [CS]');
      const withoutCs = isComingSoon ? raw.slice(0, -5) : raw;
      const pipeIdx = withoutCs.indexOf('|');
      const iconKey = pipeIdx >= 0 ? withoutCs.slice(0, pipeIdx) : null;
      const label = pipeIdx >= 0 ? withoutCs.slice(pipeIdx + 1) : withoutCs;
      return { label, isComingSoon, iconKey };
    });
  }

  return [
    {
      label: t('p.pricingPage.ultimate.perks.allUnlimited', {
        defaultValue: 'All app features with unlimited access',
      }),
      isComingSoon: false,
    },
    {
      label: t('p.pricingPage.ultimate.perks.prioritySupport', {
        defaultValue: 'Priority support',
      }),
      isComingSoon: false,
    },
    {
      label: t('p.pricingPage.ultimate.perks.vipServices', {
        defaultValue: 'VIP services',
      }),
      isComingSoon: false,
    },
  ];
}

export function createPlansAdaptor(t) {
  return function adaptPlansToUi(plansRaw) {
    const raw = safeArray(plansRaw).filter((p) => p && p.isActive !== false);

    // One shared checklist row order for every plan card (and the Plans
    // Comparison table), based on the lowest-tier plan's own included-first
    // ranking, so every plan reuses the exact same order instead of each
    // resorting independently.
    const referencePlanRaw = raw.reduce(
      (lowest, p) => (!lowest || toNumber(p?.order, 0) < toNumber(lowest?.order, 0) ? p : lowest),
      null,
    );
    const referenceFeaturesOrder = referencePlanRaw
      ? buildFeaturesUiFromPricingNodeStatuses(referencePlanRaw, t)
      : [];
    const featuresOrderIndex = new Map(referenceFeaturesOrder.map((f, i) => [f.id, i]));

    const mapped = raw
      .map((p) => {
        const name = String(p?.name || '');
        const key = normalizeKey(name);

        const isCurrent = toBool(p?.isCurrentPlan, false);
        const isRecommended = toBool(p?.isRecommended, false);

        const monthlyPrice = toNumber(p?.monthlyPrice, 0);
        const annualPrice = toNumber(p?.annualPrice, 0);

        const monthlyCompareAtPrice =
          p?.monthlyCompareAtPrice != null ? toNumber(p?.monthlyCompareAtPrice, 0) : null;

        const annualCompareAtPrice =
          p?.annualCompareAtPrice != null ? toNumber(p?.annualCompareAtPrice, 0) : null;

        const hasMonthlyCompareAt =
          monthlyCompareAtPrice != null &&
          Number.isFinite(monthlyCompareAtPrice) &&
          monthlyCompareAtPrice > 0;

        const discountPeriod = String(p?.discount?.period || '')
          .trim()
          .toLowerCase();

        const isDiscounted =
          hasMonthlyCompareAt && monthlyPrice > 0 && monthlyPrice < monthlyCompareAtPrice;

        const hasAnnual = annualPrice > 0 && monthlyPrice > 0;
        const annualPerMonth = hasAnnual ? annualPrice / 12 : 0;
        const saveAmountMonthlyRaw = hasAnnual ? monthlyPrice - annualPrice / 12 : 0;
        const saveAmountMonthly = Math.round(saveAmountMonthlyRaw * 100) / 100;

        const saveText =
          hasAnnual && saveAmountMonthly > 0
            ? t('p.pricingPage.plan.saveOverMonthly', {
                defaultValue: 'Save ${{amount}} over monthly',
                amount: saveAmountMonthly,
              })
            : null;

        const billedText = hasAnnual
          ? t('p.pricingPage.plan.billedOnceYearly', { defaultValue: 'Billed once yearly' })
          : null;

        const extraPercentRaw =
          hasAnnual && annualPerMonth > 0
            ? ((monthlyPrice - annualPerMonth) / monthlyPrice) * 100
            : 0;

        const extraPercent = Math.round(extraPercentRaw);

        const extraText =
          hasAnnual && extraPercent >= 20
            ? t('p.pricingPage.plan.extraPercentOffYear', {
                defaultValue: 'Extra {{percent}}% off for 1 year(s)',
                percent: extraPercent,
              })
            : null;

        const isUnlimitedCampaigns = toBool(p?.isUnlimitedCampaigns, false);
        const isUnlimitedVariants = toBool(p?.isUnlimitedVariants, false);

        const maxCampaignsCount = toNumber(p?.maxCampaignsCount, 0);
        const maxVariantsCount = toNumber(p?.maxVariantsCount, 0);

        const metrics = [
          {
            value: formatLimit(isUnlimitedCampaigns, maxCampaignsCount, t),
            label: t('p.pricingPage.metrics.discountCampaigns', {
              defaultValue: 'Discount campaigns',
            }),
            iconKey: 'productList',
          },
          {
            value: formatLimit(isUnlimitedVariants, maxVariantsCount, t),
            label: t('p.pricingPage.metrics.liveDiscountedVariants', {
              defaultValue: 'Live discounted variants',
            }),
            iconKey: 'discount',
          },
        ];

        const featuresUi = buildFeaturesUiFromPricingNodeStatuses(p, t, featuresOrderIndex);

        const perks = isUltimatePlan(p) ? buildUltimatePerks(p, t) : [];

        return {
          id: String(p?.id || ''),
          order: toNumber(p?.order, 0),
          key,

          name,
          subtitle: String(p?.description || ''),

          isCurrent,
          isRecommended,
          isDark: isRecommended,

          monthlyPrice,
          annualPrice,

          monthlyCompareAtPrice,
          annualCompareAtPrice,
          isDiscounted,
          discountPeriod,

          saveText,
          billedText,
          extraText,
          trialDays: Math.max(0, toNumber(p?.trialDays, 0)),

          metrics,
          featuresUi,

          perks,

          raw: p,
        };
      })
      .filter((x) => x.id);

    mapped.sort((a, b) => toNumber(a.order, 0) - toNumber(b.order, 0));

    const firstGridIdx = mapped.findIndex((x) => String(x?.key || '') !== 'starter');
    if (firstGridIdx >= 0) {
      mapped[firstGridIdx] = { ...mapped[firstGridIdx], extraText: null };
    }

    return mapped;
  };
}
